import React from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { LISTING_PAGE_PENDING_APPROVAL_VARIANT } from '../../util/urlHelpers';
import {
  SIMPLY_FIT_INSTAGRAM_URL,
  getTrainerStatusAction,
  getTrainerStatusKey,
  shouldShowTrainerStatus,
} from '../../util/trainerStatus';

import NamedLink from '../NamedLink/NamedLink';

import css from './PendingApprovalBanner.module.css';

// Eye icon used for the preview button and the read-only "preview mode" banner.
const IconEye = ({ className }) => (
  <svg
    className={className}
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

// Clock icon used for the pending heading and the "Pending" badge.
const IconClock = ({ className }) => (
  <svg
    className={className}
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

/**
 * The call-to-action that ends the banner. Which one shows is decided entirely
 * by the shared trainer status, so it can never disagree with the heading and
 * body above it or with the sitewide strip on other pages.
 */
const StatusAction = props => {
  const { status, listingParams } = props;
  const action = getTrainerStatusAction(status);

  if (action === 'plan') {
    return (
      <NamedLink
        className={css.statusButton}
        name="SubscriptionsPage"
        params={{ id: listingParams.id, slug: listingParams.slug }}
      >
        <FormattedMessage id="PendingApprovalBanner.getPlanButton" />
      </NamedLink>
    );
  }

  if (action === 'instagram') {
    return (
      <a
        className={css.statusButton}
        href={SIMPLY_FIT_INSTAGRAM_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        <FormattedMessage id="PendingApprovalBanner.verifyButton" />
      </a>
    );
  }

  if (action === 'upgrade') {
    // Straight to the plans page, where the trainer can see what each tier costs
    // and includes before switching. That page changes the tier on an existing
    // subscription in place rather than starting a second one.
    return (
      <NamedLink
        className={css.statusButton}
        name="SubscriptionsPage"
        params={{ id: listingParams.id, slug: listingParams.slug }}
      >
        <FormattedMessage id="PendingApprovalBanner.upgradeButton" />
      </NamedLink>
    );
  }

  return null;
};

/**
 * The full yellow banner shown while a trainer's profile is not live yet.
 *
 * It renders on exactly two surfaces — the trainer's own listing page and the
 * Edit Listing wizard. Everywhere else the trainer goes, the condensed
 * `TrainerStatusStrip` in the Topbar says the same thing in one line. The two
 * never appear on the same page, and both read `getTrainerLiveStatus`, so they
 * cannot drift apart.
 *
 * Heading, body and button all come from the resolved status. The surface only
 * decides which navigation extras come along:
 * - `edit` (Edit Listing wizard): a "Preview Public Profile" link and a
 *   "Pending" badge.
 * - `listing` (own listing page): a "← Back to editing" link.
 *
 * Renders nothing once the trainer is verified on Tier 2/3 — no manual stop
 * needed.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - extends the root class
 * @param {string} [props.rootClassName] - overrides the root class
 * @param {Object} props.status - from `getTrainerLiveStatus(listing)`
 * @param {'edit'|'listing'} [props.surface='edit'] - which page this is on
 * @param {Object} props.listingParams - `{ id, slug }` of the trainer's listing
 * @returns {JSX.Element|null}
 */
const PendingApprovalBanner = props => {
  const { rootClassName, className, status, surface = 'edit', listingParams } = props;

  if (!shouldShowTrainerStatus(status) || !listingParams) {
    return null;
  }

  const statusKey = getTrainerStatusKey(status);
  const isEditSurface = surface === 'edit';
  const classes = classNames(rootClassName || css.root, css.editMode, className);

  return (
    <div className={classes}>
      <div className={css.editLeft}>
        <div className={css.headingRow}>
          <IconClock className={css.headingIcon} />
          <h2 className={css.heading}>
            <FormattedMessage id={`PendingApprovalBanner.heading_${statusKey}`} />
          </h2>
        </div>
        <p className={css.body}>
          <FormattedMessage id={`PendingApprovalBanner.body_${statusKey}`} />
        </p>
      </div>
      <div className={css.editActions}>
        <StatusAction status={status} listingParams={listingParams} />

        {isEditSurface ? (
          <NamedLink
            className={css.previewButton}
            name="ListingPageVariant"
            params={{
              id: listingParams.id,
              slug: listingParams.slug,
              variant: LISTING_PAGE_PENDING_APPROVAL_VARIANT,
            }}
          >
            <IconEye className={css.previewButtonIcon} />
            <FormattedMessage id="PendingApprovalBanner.previewButton" />
          </NamedLink>
        ) : (
          <NamedLink
            className={css.previewButton}
            name="EditListingPage"
            params={{ id: listingParams.id, slug: listingParams.slug, type: 'edit', tab: 'details' }}
          >
            <FormattedMessage id="PendingApprovalBanner.backToEditing" />
          </NamedLink>
        )}

        <span className={css.pendingBadge}>
          <IconClock className={css.badgeIcon} />
          <FormattedMessage id="PendingApprovalBanner.pendingBadge" />
        </span>
      </div>
    </div>
  );
};

export default PendingApprovalBanner;
